import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { UserModel } from '../../UsersTable';
import Fetcher, { URLBuild } from '../../../services/fetcher';
import settings from '../../../settings';
import { Divider, FormControl, FormControlLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, TextField, Typography } from '@material-ui/core';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import frLocale from "date-fns/locale/fr";
import { useNotification } from '../../../contexts/Notification';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexFlow: 'row',
    flex: 1
  },
  divider: {
    margin: '0 10px'
  },
  content: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    width: 400,
    height: 400
  },
  miniTitle: {
    textDecoration: 'underline'
  },
  miniDivider: {
    marginBottom: 'revert'
  },
  multiplierInput: {
    width: 100
  },
  multiplier: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 15
  },
  radios: {
    display: 'flex',
    flexDirection: 'row'
  },
  rolePicker: {
    marginBottom: 15
  }
}));

export interface SubscriptionChange {
  role: string,
  multiplier: number,
  period: 'month' | 'year',
  endDate: Date
};

interface SelectAmountProps {
  default: SubscriptionChange | null,
  user: UserModel,
  onSubscriptionChange: (sub: SubscriptionChange) => void
};

interface UserInfo {
  active: boolean;
  startDate: number;
  endDate: number;
  paymentWay: string;
  role: string;
  userId: string;
};

const subRoles = ['Abonne', 'AbonneDecouverte', 'AbonneEtudiant', 'AbonneClassique', 'AbonnePremium', 'AbonneLegacy'];

function getDefaultRole(user: UserModel): string {
  const roles = user.roles.filter(role => subRoles.includes(role));
  if (roles.length > 0)
    return roles[0];
  return '';
}

function dateValueFormatter(value: number): string {
  const date: Date = new Date(value * 1000);
  const day = ('0' + date.getDate()).substr(-2);
  const month = ('0' + (date.getMonth() + 1)).substr(-2);
  const year = date.getFullYear();
  return day + '-' + month + '-' + year;
}

export const SelectSubscription = (props: SelectAmountProps) => {
  const classes = useStyles();
  const [currentSubInfo, setCurrentSubInfo] = useState<UserInfo | null>(null);
  const [role, setRole] = useState<string>(props.default ? props.default.role : getDefaultRole(props.user));
  const [multiplier, setMultiplier] = useState<number>(props.default ? props.default.multiplier : 1);
  const [period, setPeriod] = useState<'month' | 'year'>(props.default ? props.default.period : 'month');
  const [selectedDate, setSelectedDate] = useState<Date | null>(props.default ? props.default.endDate : null);
  const notification = useNotification();

  useEffect(() => {
    Fetcher.get(URLBuild(settings.PaymentProvider.paymentApi + '/user-sub/' + props.user.id, {}))
      .then((subInfo: UserInfo | {nothing: boolean}) => {
        if ((subInfo as {nothing: boolean}).nothing === true)
          setCurrentSubInfo(null);
        else
          setCurrentSubInfo(subInfo as UserInfo);
      }).catch((e: Error) => {
        notification.notify(e.message, "error");
        console.error(e);
      });
  }, []);

  useEffect(() => {
    const baseDate = currentSubInfo ?
      new Date (currentSubInfo.endDate * 1000) :
      new Date();
    if (period === 'month')
      baseDate.setMonth(baseDate.getMonth() + multiplier);
    else if (period === 'year')
      baseDate.setFullYear(baseDate.getFullYear() + multiplier);
    setSelectedDate(baseDate);
  }, [period, multiplier, currentSubInfo]);

  useEffect(() => {
    props.onSubscriptionChange({
      role,
      multiplier,
      period,
      endDate: selectedDate as Date
    });
  }, [role, multiplier, period, selectedDate]);

  const handleMultiplierChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let val = Number(event.target.value);
    val = val < 0 ? 0 : val;
    setMultiplier(val);
  };

  const handlePeriodChange = (_: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, value: string) => {
    setPeriod(value as 'month' | 'year');
  };

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };

  const handleRoleChange = (e: React.ChangeEvent<{value: unknown}>) => {
    setRole(e.target.value as string);
  };

  return (
    <div className={classes.container}>
      <div className={classes.content}>
        <Typography variant="h5" component="h2" gutterBottom noWrap>
          Actuellement
        </Typography>
        <Divider className={classes.miniDivider}/>
        <Typography noWrap>
          <span className={classes.miniTitle}>Adresse e-mail:</span> {props.user.email}
        </Typography>
        <Typography noWrap>
          <span className={classes.miniTitle}>Role:</span> {props.user.roles.filter(role =>
            ['Lecteur', ...subRoles].includes(role)
          ).map((role, i) => i === 0 ? role : ', ' + role)}
        </Typography>
        {currentSubInfo &&
          <div>
            <Typography noWrap>
              Du {dateValueFormatter(currentSubInfo.startDate)} jusqu'au {dateValueFormatter(currentSubInfo.endDate)}.
            </Typography>
            <Typography noWrap>
              {currentSubInfo.active ? 'Abonnement toujours actif.' : 'Abonnement inactif.'}
            </Typography>
          </div>
        }
      </div>
      <Divider orientation="vertical" flexItem className={classes.divider}/>
      <div className={classes.content}>
        <Typography variant="h5" component="h2" gutterBottom>
          Mise à jour - renouvellement
        </Typography>
        <Divider className={classes.miniDivider}/>
        <FormControl className={classes.rolePicker}>
          <InputLabel id="selectRole">Role à appliquer</InputLabel>
          <Select
            labelId="selectRole"
            value={role}
            onChange={handleRoleChange}
          >
            {
              subRoles.map((role, i) => <MenuItem key={i} value={role}>{role}</MenuItem>)
            }
          </Select>
        </FormControl>
        <div className={classes.multiplier}>
          <TextField
            label="Multiplicateur"
            type="number"
            InputLabelProps={{
              shrink: true,
            }}
            value={multiplier}
            onChange={handleMultiplierChange}
            className={classes.multiplierInput}
          />
          <RadioGroup aria-label="gender" name="gender1" value={period} className={classes.radios} onChange={handlePeriodChange}>
            <FormControlLabel value="month" control={<Radio />} label="Mois" />
            <FormControlLabel value="year" control={<Radio />} label="Année(s)" />
          </RadioGroup>
        </div>
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
          <KeyboardDatePicker
            style={{marginTop: 0}}
            disableToolbar
            variant="inline"
            format="dd/MM/yyyy"
            label="Fin de l'abonnement"
            value={selectedDate}
            onChange={handleDateChange}
            KeyboardButtonProps={{
              'aria-label': 'change date',
            }}
          />
        </MuiPickersUtilsProvider>
      </div>
    </div>
  );
}